import type { User } from "./types";

const SITE_RESOURCE_TYPE = "SITE";
const ALL = "*";

/** SITE 리소스의 하위 기능 권한 키. subResources 에 담기는 값. */
export type SiteFeature = "CCTV" | "EVENT" | "HEALTH";

// User 객체는 zustand store 의 불변 객체라 권한 변경 시에만 참조가 바뀐다.
// 목록 필터링에서 canAccessSite 가 현장 수(최대 1000)만큼 반복 호출되므로
// User 별 계산 결과를 WeakMap 으로 캐시해 O(N×M) 재계산을 방지한다.
const siteIdsCache = new WeakMap<User, Set<string>>();

// 기능(subResource)별 접근 가능한 현장 id 집합 캐시. canAccessFeature 도 목록
// 필터에서 반복 호출되므로 (user, feature) 별로 계산 결과를 캐시한다.
const featureIdsCache = new WeakMap<User, Map<string, Set<string>>>();

export function getSiteIds(user: User | null): Set<string> {
  if (!user) return new Set<string>();
  const cached = siteIdsCache.get(user);
  if (cached) return cached;

  const ids = new Set<string>();
  for (const p of user.permissions ?? []) {
    for (const rp of p.resources ?? []) {
      if (rp.resourceType === SITE_RESOURCE_TYPE && rp.resourceId) ids.add(rp.resourceId);
    }
  }
  siteIdsCache.set(user, ids);
  return ids;
}

export function canAccessSite(user: User | null, siteId: string | number): boolean {
  if (!user) return false;
  if (user.authority === "ADMIN") return true;
  const ids = getSiteIds(user);
  return ids.has(ALL) || ids.has(String(siteId));
}

export function hasNoSiteAccess(user: User | null): boolean {
  if (!user) return true;
  if (user.authority === "ADMIN") return false;
  const ids = getSiteIds(user);
  return !ids.has(ALL) && ids.size === 0;
}

/**
 * 특정 기능(CCTV/EVENT/HEALTH)에 접근 가능한 현장 id 집합.
 * "*" 가 포함되면 모든 현장에서 해당 기능 접근 가능.
 * 하위 권한(subResources)에 명시된 기능만 집계 — 빈 배열은 접근 없음.
 */
function getFeatureSiteIds(user: User, feature: string): Set<string> {
  let byFeature = featureIdsCache.get(user);
  if (!byFeature) {
    byFeature = new Map();
    featureIdsCache.set(user, byFeature);
  }
  const cached = byFeature.get(feature);
  if (cached) return cached;

  const ids = new Set<string>();
  for (const p of user.permissions ?? []) {
    for (const rp of p.resources ?? []) {
      if (rp.resourceType !== SITE_RESOURCE_TYPE || !rp.resourceId) continue;
      if (rp.subResources?.includes(feature)) ids.add(rp.resourceId);
    }
  }
  byFeature.set(feature, ids);
  return ids;
}

/**
 * 사용자가 특정 현장에서 특정 기능에 접근 가능한지.
 * - ADMIN: 전체 허용 (전체 접근은 authority 로 표현)
 * - 일반 사용자: 해당 현장(또는 "*") 권한의 subResources 에 그 기능이 포함될 때만 허용.
 *   subResources 가 비어있으면 접근 없음.
 */
export function canAccessFeature(
  user: User | null,
  siteId: string | number,
  feature: SiteFeature
): boolean {
  if (!user) return false;
  if (user.authority === "ADMIN") return true;
  const ids = getFeatureSiteIds(user, feature);
  return ids.has(ALL) || ids.has(String(siteId));
}

/** 사용자가 (어느 현장이든) 특정 기능 접근 권한을 하나라도 가졌는지 — 메뉴 노출 판단용. */
export function hasFeatureAccess(user: User | null, feature: SiteFeature): boolean {
  if (!user) return false;
  if (user.authority === "ADMIN") return true;
  return getFeatureSiteIds(user, feature).size > 0;
}
